import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const CART_DRAWER_REDIRECT_KEY = 'theme:open-cart-drawer';

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    this.#openFromRedirect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
  }

  /**
   * @param {Event} event
   */
  #onCartAdd = async (event) => {
    if (!(event instanceof CartAddEvent)) return;
    if (!this.hasAttribute('auto-open') || event.detail?.data?.source !== 'product-form-component') return;

    const addedItemKey = /** @type {{key?: string}} */ (event.detail?.resource)?.key;
    const sectionsHtml = event.detail?.data?.sections;
    const cartItemsEl = this.querySelector('cart-items-component');
    const sectionId = cartItemsEl instanceof HTMLElement ? cartItemsEl.dataset.sectionId : undefined;
    const existedBefore = addedItemKey
      ? this.querySelector(`.cart-item[data-key="${addedItemKey}"]`) !== null
      : false;

    /** @type {DialogComponent | null} */
    const quickAdd = /** @type {DialogComponent | null} */ (document.getElementById('quick-add-drawer'));
    const quickAddDialog = quickAdd?.refs?.dialog;
    const isSwapping = quickAddDialog instanceof HTMLDialogElement && quickAddDialog.open;

    if (isSwapping && quickAdd) {
      this.#openWithSwap(quickAdd, quickAddDialog, sectionId, sectionsHtml, addedItemKey, existedBefore);
    } else {
      this.#openDirect(sectionId, sectionsHtml, addedItemKey, existedBefore);
    }
  };

  /**
   * Opens cart drawer by swapping from the quick-add drawer with a view transition
   * @param {DialogComponent} quickAdd
   * @param {HTMLDialogElement} quickAddDialog
   * @param {string | undefined} sectionId
   * @param {Record<string, string> | undefined} sectionsHtml
   * @param {string | undefined} addedItemKey
   * @param {boolean} existedBefore
   */
  #openWithSwap(quickAdd, quickAddDialog, sectionId, sectionsHtml, addedItemKey, existedBefore) {
    /** @type {HTMLDialogElement} */
    const cartDialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);

    quickAddDialog.style.viewTransitionName = 'drawer-shell';

    if (!document.startViewTransition) {
      this.#morphCart(sectionId, sectionsHtml);
      quickAdd.closeDialog({ immediate: true });
      this.showDialog({ immediate: true });
      this.#focusDrawerHeader();
      this.#resetScrollArea();
      return;
    }

    /** @type {boolean} */
    let isNewItem = false;

    const transition = document.startViewTransition(() => {
      quickAddDialog.style.viewTransitionName = '';
      this.#morphCart(sectionId, sectionsHtml);
      quickAdd.closeDialog({ immediate: true });
      this.showDialog({ immediate: true });
      this.#focusDrawerHeader();
      this.#resetScrollArea();
      cartDialog.style.viewTransitionName = 'drawer-shell';
      isNewItem = this.#hideNewItem(cartDialog, addedItemKey, existedBefore);
    });

    transition.finished.then(() => {
      this.#focusDrawerHeader();
      this.#resetScrollArea();
      cartDialog.style.viewTransitionName = '';
      this.#animateAddedItem(cartDialog, addedItemKey, isNewItem);
    });
  }

  /**
   * Opens cart drawer directly (no quick-add swap)
   * @param {string | undefined} sectionId
   * @param {Record<string, string> | undefined} sectionsHtml
   * @param {string | undefined} addedItemKey
   * @param {boolean} existedBefore
   */
  #openDirect(sectionId, sectionsHtml, addedItemKey, existedBefore) {
    /** @type {HTMLDialogElement} */
    const cartDialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);

    this.#morphCart(sectionId, sectionsHtml);
    const isNewItem = this.#hideNewItem(cartDialog, addedItemKey, existedBefore);
    this.showDialog();
    this.#focusDrawerHeader();
    this.#resetScrollArea();

    if (addedItemKey) {
      cartDialog.addEventListener('animationend', () => {
        this.#animateAddedItem(cartDialog, addedItemKey, isNewItem);
      }, { once: true });
    }
  }

  /**
   * Morphs the cart section with fresh HTML
   * @param {string | undefined} sectionId
   * @param {Record<string, string> | undefined} sectionsHtml
   */
  #morphCart(sectionId, sectionsHtml) {
    if (sectionId && sectionsHtml?.[sectionId]) {
      const wasOpen = this.refs.dialog.open;
      morphSection(sectionId, sectionsHtml[sectionId], 'hydration');
      if (wasOpen) {
        this.refs.dialog.setAttribute('scroll-lock', '');
      }
    }
  }

  #resetScrollArea() {
    const scrollArea = this.refs.scrollArea;
    if (!(scrollArea instanceof HTMLElement)) return;

    scrollArea.scrollTop = 0;

    requestAnimationFrame(() => {
      scrollArea.scrollTop = 0;
    });
  }

  #focusDrawerHeader() {
    const title = this.refs.dialog.querySelector('.dialog-title');

    if (title instanceof HTMLElement) {
      title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
      return;
    }

    const closeButton = this.refs.dialog.querySelector('.icon-button--close');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus({ preventScroll: true });
    }
  }

  /**
   * Hides a newly added cart item so it can be revealed with animation
   * @param {HTMLElement} cartDialog
   * @param {string | undefined} addedItemKey
   * @param {boolean} existedBefore - Whether the item was already in the cart before this add
   * @returns {boolean} Whether the item is new
   */
  #hideNewItem(cartDialog, addedItemKey, existedBefore) {
    if (!addedItemKey) return false;

    const cartItem = cartDialog.querySelector(`.cart-item[data-key="${addedItemKey}"]`);
    if (!(cartItem instanceof HTMLElement)) return false;

    const isNew = !existedBefore;

    if (isNew) {
      cartItem.classList.add('cart-item--new');
    }

    return isNew;
  }

  /**
   * Animates the added cart item (reveal for new, quantity trace for existing)
   * @param {HTMLElement} cartDialog
   * @param {string | undefined} addedItemKey
   * @param {boolean} isNewItem
   */
  #animateAddedItem(cartDialog, addedItemKey, isNewItem) {
    if (!addedItemKey) return;

    const cartItem = cartDialog.querySelector(`.cart-item[data-key="${addedItemKey}"]`);
    if (!(cartItem instanceof HTMLElement)) return;

    if (isNewItem) {
      this.#animateReveal(cartItem);
    } else {
      this.#animateQuantityUpdate(cartItem);
    }
  }

  /**
   * Reveal animation for a new cart item (inverse of removal)
   * @param {HTMLElement} item
   */
  #animateReveal(item) {
    // Remove cart-item--new to measure the natural height
    item.classList.remove('cart-item--new');
    const naturalHeight = item.offsetHeight;

    // Collapse the item with inline styles
    item.style.overflow = 'hidden';
    item.style.opacity = '0';
    item.style.translate = '0 -10px';
    item.style.maxHeight = '0px';
    item.style.marginTop = '0px';
    // eslint-disable-next-line no-unused-expressions
    item.offsetHeight;

    // Transition to natural size
    item.classList.add('cart-item--reveal');
    item.style.maxHeight = `${naturalHeight}px`;
    item.style.opacity = '1';
    item.style.translate = '0 0';
    item.style.marginTop = '';

    item.addEventListener('transitionend', (e) => {
      if (e.target === item && e.propertyName === 'max-height') {
        item.classList.remove('cart-item--reveal');
        item.style.maxHeight = '';
        item.style.opacity = '';
        item.style.translate = '';
        item.style.overflow = '';
      }
    });
  }

  /**
   * Border trace animation on the quantity selector
   * @param {HTMLElement} item
   */
  #animateQuantityUpdate(item) {
    const qty = item.querySelector('.quantity-selector');
    if (!(qty instanceof HTMLElement)) return;

    qty.classList.add('quantity-selector--updated');
    qty.addEventListener('animationend', (e) => {
      if (/** @type {AnimationEvent} */ (e).animationName === 'qty-trace-out') {
        qty.classList.remove('quantity-selector--updated');
      }
    });
  }

  open = () => {
    this.showDialog();
  };

  close = () => {
    this.closeDialog();
  };

  #openFromRedirect() {
    try {
      if (window.localStorage.getItem(CART_DRAWER_REDIRECT_KEY) !== '1') return;

      window.localStorage.removeItem(CART_DRAWER_REDIRECT_KEY);
      requestAnimationFrame(() => this.showDialog());
    } catch {
      // Ignore storage failures and keep the drawer functional.
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
